import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputCheckboxField } from "../../../../components/Shared/InputCheckboxField";
import { request } from "../../../../utils/Request";
import OpenOrderDamageCaseDocument from "./OpenOrderDamageCaseDocument";
import { Divider } from "primereact/divider";
import { InputTextAreaField } from "../../../../components/Shared/InputTextAreaField";

interface IProps {
    visible: any;
    toastify: any;
    dropdowns: any;
    caseNumber?: string;
    onClose: any;
}

type TInputForm = {
    close_label: boolean,
    close_all_labels: boolean,
    send_notification_efl: boolean,
    send_notification_carrier: boolean,
    send_notification_compound: boolean,
    send_notification_dekra: boolean,
    comment: null | string,
};

const DEFAULT_FORM_STATE = {
    close_label: false,
    close_all_labels: false,
    send_notification_efl: false,
    send_notification_carrier: false,
    send_notification_compound: false,
    send_notification_dekra: false,
    comment: null,
};

// @ts-ignore
function OpenOrderDamageCaseUpdate(props: IProps) {
    const { toastify, dropdowns } = props;
    const formRef: any = useRef();
    const documentRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [open, setOpen] = useState<boolean>(false);
    const [documentErrors, setDocumentErrors] = useState<any>(null);
    const { t }: any = useTranslation();

    const onClose = () => {
        setOpen(false);
        props.onClose();
    };

    const onSubmit = async (payloads: TInputForm) => {
        // Get documents from child component
        const documents = documentRef.current?.getDocuments() || [];

        // Clear previous errors
        setDocumentErrors(null);

        try {
            const response = await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/${ props.visible }/upload/documents`,
                data: {
                    close_label: payloads.close_label || false,
                    close_all_labels: payloads.close_all_labels || false,
                    send_notification_efl: payloads.send_notification_efl || false,
                    send_notification_carrier: payloads.send_notification_carrier || false,
                    send_notification_compound: payloads.send_notification_compound || false,
                    send_notification_dekra: payloads.send_notification_dekra || false,
                    comments: payloads.comment,
                    damage_documents: documents.map((item: any) => ({
                        id: item.document_id,
                        title: item.title,
                        doc_type: item.doc_type
                    }))
                },
            });

            toastify(t("record updated", { item: t('damage case') }), "info");
            onClose();
        } catch (e: any) {
            if ( e.status === 422 && e.data && e.data.errors && e.data.errors.damage_documents ) {
                setDocumentErrors(e.data.errors.damage_documents);
            } else if ( e.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const onReset = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }
        // Reset documents in child component
        documentRef.current?.resetDocuments();
        setInitFormState(DEFAULT_FORM_STATE);
        setDocumentErrors(null);
    }

    // @ts-ignore
    useEffect(() => {
        if ( props.visible ) {
            setOpen(true);
            setInitFormState(DEFAULT_FORM_STATE);
            // Reset documents when opening
            documentRef.current?.resetDocuments();
            setDocumentErrors(null);
        }
    }, [props.visible]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>
                                { props.caseNumber ? t('update damage case') + ' #' + props.caseNumber : t('update damage case') }
                            </h5>
                            <p>{ t("update damage case labels and status.") }</p>
                        </div>
                    }
                    visible={ open } position="left" onHide={ onClose } blockScroll={ true }
                    style={ { width: '800px' } }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (formProps: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ formProps.handleSubmit } className={"mb-5"}>
                                    <Row className={"mt-4 md:mb-4 sm:mb-4"}>
                                        <Col sm={ 12 }>
                                            { documentErrors && (
                                                <div className="mt-2">
                                                    { documentErrors.map((error: string, index: number) => (
                                                        <div key={ index } className="text-danger small">
                                                            { error }
                                                        </div>
                                                    )) }
                                                </div>
                                            ) }
                                            <h6 className="mb-3">{ t('documents') }</h6>
                                            <OpenOrderDamageCaseDocument
                                                ref={ documentRef }
                                                dropdowns={ dropdowns }
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Row className={ "mt-4 md:mb-4 sm:mb-4" }>
                                        <Col sm={ 12 }>
                                            <label className="form-label">{ t("send notification to") }</label>
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_efl"
                                                label={ t("efl") }
                                            />
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_carrier"
                                                label={ t("carrier") }
                                            />
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_compound"
                                                label={ t("compound") }
                                            />
                                        </Col>
                                        <Col sm={ 6 } md={ 3 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="send_notification_dekra"
                                                label={ t("dekra") }
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Row className={ "mt-4 md:mb-4 sm:mb-4" }>
                                        <Col sm={ 12 }>
                                            <Field
                                                component={ InputTextAreaField }
                                                name="comment"
                                                label={ "" }
                                                placeholder={ t("enter") + ' ' + t("comment").toLowerCase() }
                                                rows={ 4 }
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Row className={ "mb-5" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="close_label"
                                                label={ t("mark as done (remove label)") }
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                component={ InputCheckboxField }
                                                name="close_all_labels"
                                                label={ t("remove all labels") }
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Row>
                                        <Col sm={ 12 }>
                                            <div className="filter-card-footer"
                                                 style={ { width: '785px' } }>
                                                <Button type={ "submit" }
                                                        disabled={ formProps.isSubmitting }
                                                        tooltip={ t("to data save into database") } className={ "ms-2" }
                                                        size={ "small" } outlined
                                                        tooltipOptions={ { position: 'top' } }
                                                >
                                                    <i className="pi pi-save me-2" />
                                                    { t("save") }
                                                </Button>
                                                <Button type={ "button" }
                                                        disabled={ formProps.isSubmitting }
                                                        onClick={ onReset }
                                                        tooltip={ t("to make all fields empty") } className={ "ms-2" }
                                                        size={ "small" } severity={ "warning" } outlined
                                                        tooltipOptions={ { position: 'top' } }
                                                >
                                                    <i className="pi pi-refresh me-2" />
                                                    { t("reset") }
                                                </Button>
                                                <Button type={ "button" }
                                                        tooltip={ t("operation cancel") } className={ "ms-2" }
                                                        size={ "small" } severity={ "danger" } outlined
                                                        tooltipOptions={ { position: 'top' } }
                                                        onClick={ onClose }
                                                >
                                                    <i className="pi pi-arrow-left me-2" />
                                                    { t("cancel") }
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

export default withTranslation()(OpenOrderDamageCaseUpdate);

